import React, { useState, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import EditUserModal from './EditUserModal';
import DeactivateConfirmDialog from './DeactivateConfirmDialog';
import DeleteUserDialog from './DeleteUserDialog';
import SetTempPasswordModal from './SetTempPasswordModal';
import { insertAuditLog, ACTION_TYPES } from '../../../services/auditLogService';

const ActiveUsersTab = ({ users, currentUser, onUserUpdated }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [editingUser, setEditingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetEmailTarget, setResetEmailTarget] = useState(null);
  const [resetEmailLoading, setResetEmailLoading] = useState(null);
  const [resetEmailSuccess, setResetEmailSuccess] = useState(null);
  const [tempPasswordTarget, setTempPasswordTarget] = useState(null);

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'global_administrator', label: 'Global Administrator' },
    { value: 'administrator', label: 'Administrator' },
    { value: 'user', label: 'User' },
    { value: 'view_only', label: 'View Only' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = !searchTerm ||
        user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.nickname?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.first_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.last_name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesRole = !roleFilter || user?.role === roleFilter;
      const matchesStatus = !statusFilter ||
        (statusFilter === 'active' && user?.is_active) ||
        (statusFilter === 'inactive' && !user?.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });

    filtered?.sort((a, b) => {
      let aVal = a?.[sortConfig?.key];
      let bVal = b?.[sortConfig?.key];
      if (sortConfig?.key === 'last_sign_in_at') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }
      if (aVal < bVal) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, roleFilter, statusFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const handleToggleStatusClick = (user) => {
    setDeactivateTarget(user);
  };

  const handleConfirmToggle = async (reason) => {
    if (!deactivateTarget) return;
    const user = deactivateTarget;
    setActionLoading(user?.id);
    const newStatus = !user?.is_active;
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: newStatus })?.eq('id', user?.id);
      if (error) throw error;

      await insertAuditLog({
        actionType: newStatus ? ACTION_TYPES?.USER_REACTIVATED : ACTION_TYPES?.USER_DEACTIVATED,
        affectedUserId: user?.id,
        affectedUserEmail: user?.email,
        beforeValue: { is_active: user?.is_active },
        afterValue: { is_active: newStatus },
        description: `${newStatus ? 'Reactivated' : 'Deactivated'} user ${user?.full_name || user?.email}${
          reason ? `. Reason: ${reason}` : ''
        }`,
        metadata: reason ? { reason } : {},
      });

      setDeactivateTarget(null);
      onUserUpdated();
    } catch (error) {
      console.error('Error toggling user status:', error?.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteTarget(user);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await insertAuditLog({
        actionType: 'User Deleted',
        affectedUserId: deleteTarget?.id,
        affectedUserEmail: deleteTarget?.email,
        beforeValue: { is_active: false, email: deleteTarget?.email, full_name: deleteTarget?.full_name },
        afterValue: null,
        description: `Permanently deleted deactivated user ${deleteTarget?.full_name || deleteTarget?.email}`,
        metadata: {},
      });

      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', deleteTarget?.id);
      if (error) throw error;

      setDeleteTarget(null);
      onUserUpdated();
    } catch (error) {
      console.error('Error deleting user:', error?.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSendPasswordReset = async (user) => {
    setResetEmailLoading(user?.id);
    setResetEmailSuccess(null);
    try {
      const { data, error } = await supabase?.functions?.invoke('admin-user-actions', {
        body: {
          action: 'send_password_reset',
          userEmail: user?.email,
        },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to send reset email');

      await insertAuditLog({
        actionType: 'Password Reset Sent',
        affectedUserId: user?.id,
        affectedUserEmail: user?.email,
        beforeValue: null,
        afterValue: null,
        description: `Admin sent a password reset email to ${user?.full_name || user?.email}`,
        metadata: {},
      });

      setResetEmailSuccess(user?.id);
      setTimeout(() => setResetEmailSuccess(null), 3000);
    } catch (err) {
      console.error('Password reset email error:', err?.message);
    } finally {
      setResetEmailLoading(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'global_administrator': return 'bg-rose-100 text-rose-700';
      case 'administrator': return 'bg-purple-100 text-purple-700';
      case 'user': return 'bg-blue-100 text-blue-700';
      case 'view_only': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <Select
          options={roleOptions}
          value={roleFilter}
          onChange={setRoleFilter}
          placeholder="Filter by role"
        />
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filter by status"
        />
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('full_name')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-accent transition-colors"
                >
                  User
                  <Icon name={getSortIcon('full_name')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-accent transition-colors"
                >
                  Role
                  <Icon name={getSortIcon('role')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('last_sign_in_at')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-accent transition-colors"
                >
                  Last Login
                  <Icon name={getSortIcon('last_sign_in_at')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-xs font-semibold text-foreground">Status</span>
              </th>
              <th className="text-right px-4 py-3">
                <span className="text-xs font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredAndSortedUsers?.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="Users" size={32} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No users found</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAndSortedUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={18} className="text-accent" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user?.full_name || 'Unnamed User'}
                        </p>
                        {user?.nickname && (
                          <p className="text-xs text-accent truncate">@{user?.nickname}</p>
                        )}
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role ? user?.role?.replace(/_/g, ' ') : 'Unknown'}
                    </span>
                    {user?.is_pm && (
                      <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        PM
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground">{formatDate(user?.last_sign_in_at)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${user?.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm text-foreground">{user?.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingUser(user)}
                        disabled={user?.id === currentUser?.id}
                        title="Edit user"
                      >
                        <Icon name="Pencil" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSendPasswordReset(user)}
                        disabled={resetEmailLoading === user?.id}
                        loading={resetEmailLoading === user?.id}
                        title="Send password reset email"
                        className={resetEmailSuccess === user?.id ? 'text-green-600' : 'hover:text-blue-600'}
                      >
                        <Icon name={resetEmailSuccess === user?.id ? 'CheckCircle' : 'Mail'} size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setTempPasswordTarget(user)}
                        title="Set temporary password"
                        className="hover:text-amber-600"
                      >
                        <Icon name="KeyRound" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleStatusClick(user)}
                        disabled={user?.id === currentUser?.id || actionLoading === user?.id}
                        loading={actionLoading === user?.id}
                        title={user?.is_active ? 'Deactivate user' : 'Reactivate user'}
                        className={user?.is_active ? 'hover:text-orange-600' : 'hover:text-green-600'}
                      >
                        <Icon name={user?.is_active ? 'UserX' : 'UserCheck'} size={16} />
                      </Button>
                      {!user?.is_active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(user)}
                          title="Delete user permanently"
                          className="hover:text-red-600"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <EditUserModal
        isOpen={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onUserUpdated={() => {
          onUserUpdated();
          setEditingUser(null);
        }}
      />
      <DeactivateConfirmDialog
        isOpen={!!deactivateTarget}
        user={deactivateTarget}
        onConfirm={handleConfirmToggle}
        onCancel={() => setDeactivateTarget(null)}
        loading={actionLoading === deactivateTarget?.id}
      />
      <DeleteUserDialog
        isOpen={!!deleteTarget}
        user={deleteTarget}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteLoading}
      />
      <SetTempPasswordModal
        isOpen={!!tempPasswordTarget}
        user={tempPasswordTarget}
        onClose={() => setTempPasswordTarget(null)}
      />
    </div>
  );
};

export default ActiveUsersTab;